import React from "react";
import { Document, Page, pdf } from "@react-pdf/renderer";
import { PAGE } from "./layout/constants";
import { chunkIntoPages, slotForIndex } from "./sheetComposer";
import { generateQrDataUrl } from "./qrCode";
import LabelTagPdf from "./LabelTagPdf";
import { composeSheetWithUnits } from "./composeSheetWithUnits";

/** Blob.arrayBuffer não existe no jsdom do CRA; FileReader cobre os dois lados. */
const blobToArrayBuffer = (blob) => {
  if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
};

const attachQrDataUrls = async (cards) =>
  Promise.all(
    cards.map(async (card) => ({
      ...card,
      qrDataUrl: card.qrDataUrl || (await generateQrDataUrl(card.serial)),
    }))
  );

/**
 * Overlay transparente com texto + QR. Moldura vem das unidades carimbadas
 * em composeSheetWithUnits — só slots ocupados recebem arte.
 */
export function PaperDocument({ cards }) {
  const pages = chunkIntoPages(cards, 10);
  return (
    <Document>
      {pages.map((pageCards, pageIndex) => (
        <Page
          key={`page-${pageIndex}`}
          size={[PAGE.width, PAGE.height]}
          style={{ position: "relative" }}
        >
          {pageCards.map((card, indexOnPage) => (
            <LabelTagPdf
              key={card.serial || `slot-${indexOnPage}`}
              card={card}
              slot={slotForIndex(indexOnPage)}
            />
          ))}
        </Page>
      ))}
    </Document>
  );
}

/**
 * PDF multipage de um papel de estoque (dourado / majorca / colorido).
 * Cada carta carimba a unidade do seu visual; molduras vazias não aparecem.
 */
export async function renderPaperPdfBlob(cards) {
  const cardsWithQr = await attachQrDataUrls(cards);
  const overlayBlob = await pdf(<PaperDocument cards={cardsWithQr} />).toBlob();
  const overlayBytes = await blobToArrayBuffer(overlayBlob);
  const merged = await composeSheetWithUnits(overlayBytes, cardsWithQr);
  return new Blob([merged], { type: "application/pdf" });
}

/** @deprecated alias — testes antigos; preferir renderPaperPdfBlob */
export async function renderBucketPdfBlob(cards, _bucket) {
  return renderPaperPdfBlob(cards);
}

export { attachQrDataUrls };
